import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { CompanyFilters, CompanyType, CompanyStatus } from '../types/company.types';
import { COMPANY_TYPE_CONFIG, COMPANY_STATUS_CONFIG } from '../types/company.types';

interface CompaniesFiltersProps {
  filters: CompanyFilters;
  onFiltersChange: (filters: CompanyFilters) => void;
  /**
   * Affiche le filtre prospect/client. Désactivé pour les Tenants BPA
   * (scope='tenants'), où une société est toujours un client de la plateforme.
   */
  showTypeFilter?: boolean;
}

export function CompaniesFilters({
  filters,
  onFiltersChange,
  showTypeFilter = true,
}: CompaniesFiltersProps) {
  const hasFilters =
    filters.search || filters.companyType || filters.status || filters.city;

  const handleClearFilters = () => {
    onFiltersChange({});
  };

  return (
    <div className="flex flex-col sm:flex-row gap-3">
      {/* Recherche */}
      <div className="relative flex-1 max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher par nom, SIRET, email..."
          value={filters.search || ''}
          onChange={(e) =>
            onFiltersChange({ ...filters, search: e.target.value || undefined })
          }
          className="pl-9"
        />
      </div>

      {/* Filtre par type */}
      {showTypeFilter && (
        <Select
          value={filters.companyType || 'all'}
          onValueChange={(value) =>
            onFiltersChange({
              ...filters,
              companyType: value === 'all' ? undefined : (value as CompanyType),
            })
          }
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            {(Object.keys(COMPANY_TYPE_CONFIG) as CompanyType[]).map((type) => (
              <SelectItem key={type} value={type}>
                {COMPANY_TYPE_CONFIG[type].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Filtre par statut */}
      <Select
        value={filters.status || 'all'}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            status: value === 'all' ? undefined : (value as CompanyStatus),
          })
        }
      >
        <SelectTrigger className="w-[150px]">
          <SelectValue placeholder="Statut" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tous les statuts</SelectItem>
          {(Object.keys(COMPANY_STATUS_CONFIG) as CompanyStatus[]).map((status) => (
            <SelectItem key={status} value={status}>
              {COMPANY_STATUS_CONFIG[status].label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Bouton reset */}
      {hasFilters && (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClearFilters}
          className="shrink-0"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
